import { useMutation, useQueryClient } from "@tanstack/react-query";

import { API_URL } from "@/api/application-api";
import { useAuth } from "@/context/auth-context";
import { useUser } from "@/context/user-context";
import { handleResponse } from "@/utils/errorResponseHandlers";
import { jsonAuthHeaders } from "@/utils/headers";

export const useDeleteGoogleFileSearchDocument = (provider = "google") => {
  const auth = useAuth();
  const userContext = useUser();
  const workspaceId = userContext?.activeWorkspaceId;
  const qc = useQueryClient();

  return useMutation(
    async (params: {
      storeName: string;
      documentName: string;
      fileName?: string;
      fileId?: string;
      filename?: string;
    }) => {
      const response = await fetch(
        `${API_URL}/api/dashboard/v2/google-file-search-store/document/delete`,
        {
          method: "POST",
          headers: jsonAuthHeaders(auth!.userToken!),
          body: JSON.stringify({
            store_name: params.storeName,
            document_name: params.documentName,
            file_name: params.fileName,
            file_id: params.fileId,
            filename: params.filename,
            workspace_id: workspaceId,
            provider,
          }),
        },
      ).then(handleResponse);

      if (response && typeof response === "object" && "success" in response) {
        if (response.success === false) {
          const errorMessage =
            (response as { message?: string }).message || "Failed to delete document";
          throw new Error(errorMessage);
        }
      }

      return response;
    },
    {
      onSuccess: (_data, variables) => {
        qc.invalidateQueries({
          queryKey: [
            "google-file-search-store-documents",
            variables.storeName,
            workspaceId,
            provider,
          ],
        });
        qc.invalidateQueries({
          queryKey: ["google-file-search-stores", workspaceId, provider],
        });
      },
    },
  );
};
